<script setup lang="ts">
defineProps<{
  title?: string;
  description?: string;
}>();
</script>

<template>
  <section class="nx-settings-card">
    <header v-if="title || description || $slots.actions" class="nx-settings-card-head">
      <div class="nx-settings-card-titles">
        <h4 v-if="title">{{ title }}</h4>
        <p v-if="description">{{ description }}</p>
      </div>
      <div v-if="$slots.actions" class="nx-settings-card-actions">
        <slot name="actions" />
      </div>
    </header>
    <slot />
  </section>
</template>

<style scoped>
/* In a grid of cards a row stretches every card to its tallest one: the
   content still flows from the top instead of spreading over that height.
   The card is a resting surface: fill, border, radius and shadow are the
   design's (theme/design/CONTRACT.md) — a card look without a border, a
   hairline look with the divider border and nothing else. */
.nx-settings-card {
  display: grid;
  align-content: start;
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-sm);
  background: var(--nxd-surface-bg);
  border: var(--nxd-surface-border);
  border-radius: var(--nxd-surface-radius);
  box-shadow: var(--nxd-surface-shadow);
}

/* Title and description on the left, the actions on the right. When the card
   gets too narrow for both (a side column, a phone, ~420 px with two
   buttons), the titles keep their room and the actions wrap below them. */
.nx-settings-card-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--nxt-space-sm);
}

.nx-settings-card-titles {
  flex: 1 1 calc(var(--nxt-size-unit) * 12);
  min-width: 0;
}

.nx-settings-card-titles h4 {
  margin: 0;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-semibold);
}

/* A readable line length, not the full width of a wide card. */
.nx-settings-card-titles p {
  max-width: 70ch;
  margin: var(--nxt-space-2xs) 0 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
}

.nx-settings-card-actions {
  display: inline-flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  align-items: center;
  max-width: 100%;
  gap: var(--nxt-space-xs);
}
</style>
